import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QUIZ_QUESTIONS,
  prepareShuffledQuestions,
  mulberry32,
  buildQuizResult,
  getMaxScores,
} from '../../data/quizData';
import {
  loadQuizState,
  persistQuizState,
  clearQuizState,
  emptyScores,
  recomputeScoresFromAnswers,
  stripAnswersAfter,
  markQuizCompletedOnce,
  hasCompletedQuizOnce,
} from '../../data/quizStorage';
import QuizProgress from './QuizProgress';
import QuizQuestion from './QuizQuestion';
import QuizEmailCapture from './QuizEmailCapture';
import QuizSegmentStep from './QuizSegmentStep';
import QuizWelcomeHero from './QuizWelcomeHero';
import { ChevronLeft } from 'react-bootstrap-icons';
import './Quiz.css';

const makeSeed = () =>
  (Math.floor(Math.random() * 0xffffffff) >>> 0) ^ (Date.now() & 0xffffffff);

const QuizWizard = () => {
  const navigate = useNavigate();

  const [hydrated, setHydrated] = useState(false);
  const [phase, setPhase] = useState('welcome');
  const [traderSegment, setTraderSegment] = useState(null);
  const [seed, setSeed] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [scores, setScores] = useState(emptyScores);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const saved = loadQuizState();
    if (saved?.phase === 'segment') {
      setPhase('segment');
      setTraderSegment(saved.traderSegment);
      setSeed(saved.seed);
      setQuestionIndex(saved.questionIndex);
      setScores(saved.scores);
      setAnswers(saved.answers || {});
    } else if (saved?.phase === 'questions' && saved.seed != null) {
      const loadedAnswers = saved.answers || {};
      const sq = prepareShuffledQuestions(QUIZ_QUESTIONS, mulberry32(saved.seed >>> 0));
      const fixedScores = recomputeScoresFromAnswers(sq, loadedAnswers, saved.questionIndex);
      setPhase('questions');
      setTraderSegment(saved.traderSegment);
      setSeed(saved.seed);
      setQuestionIndex(saved.questionIndex);
      setScores(fixedScores);
      setAnswers(loadedAnswers);
    } else if (saved?.phase === 'results') {
      markQuizCompletedOnce();
      setPhase('results');
      setTraderSegment(saved.traderSegment);
      setSeed(saved.seed);
      setQuestionIndex(saved.questionIndex);
      setScores(saved.scores);
      setAnswers(saved.answers || {});
    } else {
      setPhase('welcome');
    }
    setHydrated(true);
  }, []);

  const shuffledQuestions = useMemo(() => {
    if (seed == null) return [];
    return prepareShuffledQuestions(QUIZ_QUESTIONS, mulberry32(seed >>> 0));
  }, [seed]);

  const maxScores = useMemo(() => getMaxScores(), []);

  const quizResultsComplete = useMemo(() => {
    if (phase !== 'results') return false;
    if (seed == null || shuffledQuestions.length === 0) return false;
    return Object.keys(answers).length >= QUIZ_QUESTIONS.length;
  }, [phase, seed, shuffledQuestions.length, answers]);

  const result = useMemo(() => {
    if (phase !== 'results') return null;
    if (shuffledQuestions.length === 0) return null;
    return buildQuizResult(scores, maxScores);
  }, [phase, scores, maxScores, shuffledQuestions.length]);

  const goToSegment = useCallback(() => {
    const initial = emptyScores();
    persistQuizState({
      phase: 'segment',
      traderSegment: null,
      seed: null,
      questionIndex: 0,
      scores: initial,
      answers: {},
    });
    setTraderSegment(null);
    setSeed(null);
    setQuestionIndex(0);
    setScores(initial);
    setAnswers({});
    setPhase('segment');
  }, []);

  const startQuestionsAfterSegment = useCallback((segmentId) => {
    const nextSeed = makeSeed();
    const initial = emptyScores();
    persistQuizState({
      phase: 'questions',
      traderSegment: segmentId,
      seed: nextSeed,
      questionIndex: 0,
      scores: initial,
      answers: {},
    });
    setTraderSegment(segmentId);
    setSeed(nextSeed);
    setQuestionIndex(0);
    setScores(initial);
    setAnswers({});
    setPhase('questions');
  }, []);

  const handleQuestionBack = useCallback(() => {
    if (shuffledQuestions.length === 0) return;
    if (questionIndex <= 0) {
      persistQuizState({
        phase: 'segment',
        traderSegment,
        seed: null,
        questionIndex: 0,
        scores: emptyScores(),
        answers: {},
      });
      setSeed(null);
      setQuestionIndex(0);
      setScores(emptyScores());
      setAnswers({});
      setPhase('segment');
      return;
    }
    const newIndex = questionIndex - 1;
    const stripped = stripAnswersAfter(shuffledQuestions, answers, newIndex + 1);
    const nextScores = recomputeScoresFromAnswers(shuffledQuestions, stripped, newIndex);
    persistQuizState({
      phase: 'questions',
      traderSegment,
      seed,
      questionIndex: newIndex,
      scores: nextScores,
      answers: stripped,
    });
    setAnswers(stripped);
    setScores(nextScores);
    setQuestionIndex(newIndex);
  }, [shuffledQuestions, questionIndex, answers, traderSegment, seed]);

  const handleSelect = useCallback(
    (option) => {
      if (phase !== 'questions' || seed == null || shuffledQuestions.length === 0) return;
      const q = shuffledQuestions[questionIndex];
      let nextAnswers = { ...answers, [q.id]: option.id };
      nextAnswers = stripAnswersAfter(shuffledQuestions, nextAnswers, questionIndex + 1);
      const nextIndex = questionIndex + 1;
      const nextScores = recomputeScoresFromAnswers(shuffledQuestions, nextAnswers, nextIndex);

      if (nextIndex >= QUIZ_QUESTIONS.length) {
        markQuizCompletedOnce();
        persistQuizState({
          phase: 'results',
          traderSegment,
          seed,
          questionIndex: QUIZ_QUESTIONS.length,
          scores: nextScores,
          answers: nextAnswers,
        });
        setAnswers(nextAnswers);
        setScores(nextScores);
        setQuestionIndex(QUIZ_QUESTIONS.length);
        setPhase('results');
      } else {
        persistQuizState({
          phase: 'questions',
          traderSegment,
          seed,
          questionIndex: nextIndex,
          scores: nextScores,
          answers: nextAnswers,
        });
        setAnswers(nextAnswers);
        setScores(nextScores);
        setQuestionIndex(nextIndex);
      }
    },
    [phase, seed, questionIndex, shuffledQuestions, answers, traderSegment],
  );

  const handleRetake = useCallback(() => {
    clearQuizState();
    setTraderSegment(null);
    setSeed(null);
    setQuestionIndex(0);
    setScores(emptyScores());
    setAnswers({});
    setPhase('welcome');
    navigate('/quiz', { replace: true });
  }, [navigate]);

  if (!hydrated) {
    return (
      <div className="quiz-shell d-flex justify-content-center align-items-center py-5">
        <div className="text-muted small">Loading…</div>
      </div>
    );
  }

  if (phase === 'welcome') {
    return (
      <QuizWelcomeHero
        onContinue={goToSegment}
        showArchetypesOverviewLink={hasCompletedQuizOnce()}
      />
    );
  }

  const currentQuestion = shuffledQuestions[questionIndex];
  const selectedOptionId =
    phase === 'questions' && currentQuestion ? answers[currentQuestion.id] : undefined;

  return (
    <div
      className="quiz-shell mx-auto px-3 px-md-4 py-4"
      style={{ maxWidth: 640 }}
    >
      {phase === 'segment' && (
        <div className="quiz-segment-wrap">
          <div className="d-flex justify-content-center mb-3">
            <button
              type="button"
              className="btn btn-link btn-sm quiz-inline-nav-btn d-inline-flex align-items-center gap-1"
              onClick={() => {
                clearQuizState();
                setTraderSegment(null);
                setSeed(null);
                setQuestionIndex(0);
                setScores(emptyScores());
                setAnswers({});
                setPhase('welcome');
              }}
            >
              <ChevronLeft size={18} aria-hidden className="quiz-inline-nav-chevron" />
              Back
            </button>
          </div>
          <QuizSegmentStep onSelect={startQuestionsAfterSegment} />
        </div>
      )}

      {phase === 'questions' && shuffledQuestions.length > 0 && currentQuestion && (
        <div className="quiz-active">
          <div className="d-flex justify-content-center mb-2">
            <button
              type="button"
              className="btn btn-link btn-sm quiz-inline-nav-btn d-inline-flex align-items-center gap-1"
              onClick={handleQuestionBack}
            >
              <ChevronLeft size={18} aria-hidden className="quiz-inline-nav-chevron" />
              Back
            </button>
          </div>
          <QuizProgress currentIndex={questionIndex} total={QUIZ_QUESTIONS.length} />
          <QuizQuestion
            prompt={currentQuestion.prompt}
            options={currentQuestion.options}
            selectedOptionId={selectedOptionId}
            onSelect={handleSelect}
          />
        </div>
      )}

      {phase === 'results' && result && quizResultsComplete ? (
        <QuizEmailCapture
          result={result}
          traderSegment={traderSegment}
          answers={answers}
          onRetake={handleRetake}
        />
      ) : null}
    </div>
  );
};

export default QuizWizard;
